import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type { AddressCreateRequest, AddressDto, AddressEditRequest } from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";

const useCreateAddress = () => {
  const queryClient = useQueryClient();
  const api = useApi();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: AddressCreateRequest) => api.postAddressesV1(data),
    onSuccess() {
      showFlashToast({
        variant: "success",
        message: t("page.addresses.create.notification.success"),
      });
    },
    onError() {
      showFlashToast({
        variant: "alert",
        message: t("page.addresses.create.notification.failed"),
      });
    },
    onSettled() {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ADDRESS_LIST(projectId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ADDRESS_LIST_INFINITE(projectId) });
    },
  });
};

const useBulkUploadAddress = () => {
  const queryClient = useQueryClient();
  const api = useApi();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (data: AddressCreateRequest) => api.postAddressesV1(data),
    onSettled() {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ADDRESS_LIST(projectId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ADDRESS_LIST_INFINITE(projectId) });
    },
  });
};

const useUpdateAddress = (addressId: string) => {
  const queryClient = useQueryClient();
  const api = useApi();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: AddressEditRequest) => api.putAddressesV1(addressId, data),
    onSuccess() {
      showFlashToast({
        variant: "success",
        message: t("page.addresses.edit.notification.success"),
      });
    },
    onError() {
      showFlashToast({
        variant: "alert",
        message: t("page.addresses.edit.notification.failed"),
      });
    },
    onSettled() {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ADDRESS_LIST(projectId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ADDRESS_LIST_INFINITE(projectId) });
    },
  });
};

const useDeleteAddress = () => {
  const queryClient = useQueryClient();
  const api = useApi();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (address: AddressDto) => api.deleteAddressesByIdV1(address.id),
    onSuccess() {
      showFlashToast({
        variant: "success",
        message: t("page.addresses.actions.delete.notifications.success"),
      });
    },
    onError() {
      showFlashToast({
        variant: "alert",
        message: t("page.addresses.actions.delete.notifications.failed"),
      });
    },
    onSettled() {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ADDRESS_LIST_INFINITE(projectId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ADDRESS_LIST(projectId) });
    },
  });
};

const useRegenerateLocationCode = () => {
  const queryClient = useQueryClient();
  const api = useApi();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (addressId: string) => api.putAddressesRegenerateLocationCodeV1(addressId),
    onSuccess() {
      showFlashToast({
        variant: "success",
        message: t("page.addresses.actions.refresh-location-code.notifications.success"),
      });
    },
    onError() {
      showFlashToast({
        variant: "alert",
        message: t("page.addresses.actions.refresh-location-code.notifications.failed"),
      });
    },
    onSettled(_data, _error, addressId) {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ADDRESS_LIST(projectId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ADDRESS_LIST_INFINITE(projectId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ADDRESS_DETAILS(projectId, addressId) });
    },
  });
};

export const addressMutations = {
  useCreateAddress,
  useBulkUploadAddress,
  useUpdateAddress,
  useDeleteAddress,
  useRegenerateLocationCode,
};
